/**
 * Zod v4 schema for `.planning/audits/definitions.json` (AUDIT-08).
 *
 * The live JSON contains a top-level object with `schema_version`, `memo_ref`, and a
 * `metrics` array of 15-field metric entries. This schema is the runtime guard that
 * fails the build if AUDIT-08 sign-off changes the shape without updating consumers.
 *
 * Source of truth for the field set: `.planning/audits/AUDIT-08-definitions.md` §
 * "Schema (definitions.json schema documentation)" (15 keys per metric).
 *
 * Cross-references:
 * - METRIC-01 expectations: see plan 01-01 success criteria + RESEARCH.md UX-04.
 * - `cci_model_name` is NOT a top-level field; the placeholder string
 *   "TBD-CCI-MODEL-NAME" lives inside `verify[]` until AUDIT-02 signs off
 *   (Plan deviation note in loader.test.ts explains why).
 */
import { z } from 'zod';

/**
 * Branded metric_id literal — accepts METRIC-NN (original 16 CMO/CFO/CRO
 * metrics), CRO-NN (Phase 4 Paul Report metrics), and MAIN-NN (Phase 5
 * John-xlsx Main Sales Dashboard tiles).
 *
 * Schema v1.1 extension (2026-06-05): the regex union `/^(METRIC|CRO)-\d{2}$/`
 * was backward-compatible — all existing METRIC-01..16 entries continued to
 * validate.
 *
 * Schema v1.2 extension (2026-06-11, Phase 5 Plan 02 Task 1): adds MAIN-NN
 * for John Moore's canonical `Dashboard Contents (1).xlsx` tile inventory
 * (~50 tiles across Sales Activity / Marketing / ROI tabs). Additive — all
 * v1.1 entries continue to validate. See Phase 5 Plan 02 PLAN.md.
 */
export const MetricIdSchema = z
  .string()
  .regex(/^(METRIC|CRO|MAIN)-\d{2}$/, 'metric_id must match /^(METRIC|CRO|MAIN)-\\d{2}$/');

/**
 * AUDIT-08 categories.
 *
 * Schema v1.2 (Phase 5 Plan 02): adds Activity / Pipeline / Pipeline Efficiency /
 * Marketing / ROI to support John xlsx tile categories.
 */
export const CategorySchema = z.enum([
  'Acquisition',
  'Revenue',
  'Efficiency',
  'Engagement',
  'Funnel',
  // Phase 5 Plan 02 — xlsx main-dashboard tabs:
  'Activity',
  'Pipeline',
  'Pipeline Efficiency',
  'Marketing',
  'ROI',
]);

/**
 * AUDIT-08 source-of-truth-system options.
 *
 * NOTE on schema drift (2026-05-26): AUDIT-08-definitions.md § Schema names six
 * canonical values (`Salesforce | Rillet | Hybrid (SF + Rillet) |
 * Hybrid (SF + Admin-entered) | Computed | Admin-entered`), but the live
 * `definitions.json` for METRIC-02 uses the legacy variant
 * `"Rillet + Salesforce (joined)"` instead of `"Hybrid (SF + Rillet)"`.
 * The audit doc is authoritative; the JSON should be regenerated to match.
 * Until that cleanup ships, we accept a free-form string so the build doesn't
 * block on a definitional inconsistency that belongs to Plan 00-06's surface.
 * Plan 04 (the SOQL layer) does not branch on this value's exact text.
 *
 * Once the JSON is reconciled, this can be tightened to `z.enum([...])` again.
 */
export const SourceOfTruthSystemSchema = z.string().min(1);

/** AUDIT-08 confidence levels. */
export const ConfidenceSchema = z.enum(['high', 'medium', 'low']);

/** AUDIT-08 low-confidence dispositions. */
export const LowConfidenceDispositionSchema = z.enum(['show', 'low-confidence-badge', 'defer']);

/** AUDIT-08 owner sign-off roles. */
export const OwnerSignoffRoleSchema = z.enum(['CRO', 'CMO', 'CFO']);

/**
 * Schema v1.1 — 5 optional CRO-aware fields added per D-2.4 (Phase 4 Plan 04-02).
 * These fields are absent from all existing METRIC-01..16 entries; Zod `.optional()`
 * makes the schema backward-compatible. AUDIT-08a addendum at `signed_with_caveats`
 * covers this additive extension (non-breaking; full re-sign-off not required per
 * RESEARCH.md §2).
 */

/** Target period scope for CRO tile overlay (D-2.4 + D-2.3 seed rows). */
export const TargetPeriodScopeSchema = z.enum(['annual', 'quarterly', 'per_ae_weekly', 'per_ae']);

/** Tile value unit for rendering / threshold computation. */
export const ValueUnitSchema = z.enum(['USD', 'COUNT', 'PCT']);

/** Tile render shape — drives SPA component selection. */
export const RenderShapeSchema = z.enum(['big_number', 'trend_with_sparkline']);

/**
 * Single metric definition entry — 15 core fields per AUDIT-08-definitions.md § Schema,
 * plus 5 optional CRO-aware fields introduced in schema v1.1 (Phase 4 Plan 04-02 Task 3).
 *
 * `.strict()` is retained: it still rejects undocumented keys, enforcing that any new
 * field additions go through this schema (and an AUDIT-08x addendum) first. The 5
 * optional v1.1 fields are explicitly declared so `.strict()` accepts CRO entries.
 *
 * For `definition_memo_ref`, the regex accepts both METRIC-NN refs (AUDIT-08) and
 * CRO-NN refs (AUDIT-08a). Format: `<audit-file>#<id-slug>`.
 */
export const DefinitionSchema = z
  .object({
    metric_id: MetricIdSchema,
    name: z.string().min(1),
    category: CategorySchema,
    formula_plain_english: z.string().min(1),
    source_of_truth_system: SourceOfTruthSystemSchema,
    /**
     * Empty string is valid for Computed/Admin-entered metrics; otherwise SOQL text.
     *
     * v1.2 (Phase 5 Plan 02): nullable to support `placeholder: true` Finance-blocked
     * tiles (MAIN-37..43) whose SOQL is genuinely undefined until the Finance ingest
     * path is resolved.
     */
    source_soql: z.string().nullable(),
    salesforce_fields_used: z.array(z.string()),
    confidence: ConfidenceSchema,
    verify: z.array(z.string()),
    /** Rillet endpoint name, or null for SF-only / Admin-entered metrics. */
    rillet_endpoint: z.string().nullable(),
    ocr_dependent: z.boolean(),
    /** Coverage % floor; null for non-OCR-dependent metrics. */
    ocr_floor_pct: z.number().int().min(0).max(100).nullable(),
    low_confidence_disposition: LowConfidenceDispositionSchema,
    owner_signoff_required: z.array(OwnerSignoffRoleSchema).min(1),
    /**
     * Anchor link back to the defining audit memo.
     * v1.0: `AUDIT-08-definitions.md#metric-NN`
     * v1.1: also accepts `AUDIT-08a-cro-schema-extension.md#cro-NN`
     * v1.2: also accepts `phases/05-headline-metrics-rillet-hubspot-board/05-RESEARCH.md#main-NN`
     *       (xlsx-canonical Main tiles per Phase 5 Plan 02 Task 1).
     * v1.3: also accepts `marketing-effectiveness-YYYY-MM-DD#main-NN`
     *       (marketing-effectiveness tiles MAIN-49..51, added 2026-07-22).
     */
    definition_memo_ref: z
      .string()
      .regex(
        /^(AUDIT-08-definitions\.md#metric-\d{2}|AUDIT-08a-cro-schema-extension\.md#cro-\d{2}|phases\/05-headline-metrics-rillet-hubspot-board\/05-RESEARCH\.md#main-\d{2}|marketing-effectiveness-\d{4}-\d{2}-\d{2}#main-\d{2})$/,
      ),

    // ---- v1.1 optional fields (Phase 4 Plan 04-02 Task 3 / AUDIT-08a) ----

    /**
     * How the target for this metric is scoped.
     * - 'annual': fiscal-year aggregate (CRO-01/04/08/09)
     * - 'quarterly': fiscal-quarter aggregate (CRO-02/03/10)
     * - 'per_ae_weekly': per-AE per-week rate (CRO-06)
     * - 'per_ae': per-AE static count (CRO-07)
     * Absent or null: metric has no target overlay (CRO-05/CRO-11).
     */
    target_period_scope: TargetPeriodScopeSchema.nullable().optional(),

    /**
     * Coverage multiplier for "3x coverage" indicator tiles (CRO-02/03/04/08/09).
     * null: no multiplier (direct-value tiles like CRO-01 or per-AE tiles).
     */
    target_multiplier: z.number().nullable().optional(),

    /**
     * Green / amber threshold bands as fractions of target (D-2.4 defaults: green=1.0, amber=0.8).
     * Consumers apply these defaults if the field is absent or null.
     */
    threshold_bands: z
      .object({ green: z.number(), amber: z.number() })
      .nullable()
      .optional(),

    /**
     * Unit for the metric value — drives currency formatting (USD), integer display
     * (COUNT), or percentage display (PCT).
     */
    value_unit: ValueUnitSchema.optional(),

    /**
     * SPA component variant for this tile.
     * - 'big_number': standard scorecard tile (most CRO metrics)
     * - 'trend_with_sparkline': CRO-06 + CRO-07 trend tiles (reads agg_cro_scorecard_trend)
     */
    render_shape: RenderShapeSchema.nullable().optional(),

    // ---- v1.2 optional field (Phase 5 Plan 02 Task 1) ----

    /**
     * Marks a tile as Finance-blocked placeholder (MAIN-37..43).
     * When true, the tile renders PlaceholderCard with `Awaiting Finance ingest
     * path` text from `agg_main_scorecard.placeholder_text`. `source_soql` may
     * be null for placeholder tiles.
     */
    placeholder: z.boolean().optional(),

    // ---- v1.2 audit-trail fields (Phase 5 Plan 05 cleanup, 2026-06-12) ----
    //
    // When the 2026-06-12 xlsx-only scope pivot dropped HubSpot + deferred Rillet,
    // affected METRIC-NN entries were KEPT in definitions.json (audit history
    // matters for AUDIT-08 sign-off) and annotated with these 5 optional fields
    // so consumers can filter them out without losing the definition memo.
    // Out-of-scope metrics use dropped_*; Phase-6-deferred metrics use deferred_*.

    /** Lifecycle marker for metrics dropped or deferred post-AUDIT-08 sign-off. */
    status: z.enum(['out-of-scope-v1', 'deferred-to-phase-6']).optional(),

    /** ISO date the metric was dropped (out-of-scope-v1 only). */
    dropped_date: z.string().optional(),

    /** Plan / commit reference that dropped the metric (out-of-scope-v1 only). */
    dropped_by: z.string().optional(),

    /** ISO date the metric was deferred (deferred-to-phase-6 only). */
    deferred_date: z.string().optional(),

    /** Plan / commit reference that deferred the metric (deferred-to-phase-6 only). */
    deferred_by: z.string().optional(),

    /** Successor metric_ids that supersede this one (empty array if none). */
    superseded_by: z.array(z.string()).optional(),

    /** Human-readable rationale for the drop / deferral. */
    reason: z.string().optional(),
  })
  .strict();

/**
 * Top-level file shape: `schema_version`, `memo_ref`, and the `metrics` array.
 *
 * schema_version history:
 *   '1.0' — original 15-field schema (METRIC-01..16)
 *   '1.1' — adds 5 optional CRO fields + CRO-NN metric_id support (Phase 4 Plan 04-02)
 *   '1.2' — adds MAIN-NN metric_id support + 5 xlsx categories
 *           (Activity / Pipeline / Pipeline Efficiency / Marketing / ROI),
 *           optional `placeholder` field, `source_soql: null` for Finance-blocked
 *           tiles, and a new memo_ref path namespace pointing at the Phase 5
 *           RESEARCH document. Additive — all v1.1 entries continue to validate.
 *
 * The schema accepts '1.0', '1.1', and '1.2' values so existing snapshot fixtures
 * remain valid. Downstream consumers that REQUIRE v1.1 features should assert
 * `file.schema_version >= '1.1'`; consumers requiring MAIN-NN entries should
 * assert `file.schema_version >= '1.2'` at load time.
 */
export const DefinitionsFileSchema = z
  .object({
    schema_version: z.enum(['1.0', '1.1', '1.2']),
    memo_ref: z.string().min(1),
    metrics: z.array(DefinitionSchema).min(1),
  })
  .strict();

/** Inferred TS types — re-exported from `index.ts` as the public API. */
export type Definition = z.infer<typeof DefinitionSchema>;
export type DefinitionsFile = z.infer<typeof DefinitionsFileSchema>;
export type TargetPeriodScope = z.infer<typeof TargetPeriodScopeSchema>;
export type ValueUnit = z.infer<typeof ValueUnitSchema>;
export type RenderShape = z.infer<typeof RenderShapeSchema>;

/**
 * METRIC-01-specific type alias — narrows `metric_id` to the literal "METRIC-01"
 * so consumers (e.g., the SPA UX-04 tooltip in Plan 05) get compile-time safety
 * that they are reading the right tile's definition.
 */
export type Metric01Definition = Definition & { metric_id: 'METRIC-01' };
